/**
 * Smooth modifier — pure typed-array core (Three.js-free).
 *
 * Applies iterative uniform-weight Laplacian smoothing to vertex positions.
 * Each pass moves each in-scope vertex toward the average of its
 * edge-connected neighbors, blended by `strength`. Topology is preserved:
 * indices, UVs, edges, morph arrays, groups, and topology pass through
 * unchanged; normals are cleared so the renderer recomputes correct shading
 * normals for the smoothed geometry.
 *
 * ## Algorithm
 *
 * For each iteration, and for each eligible vertex v:
 *   new_pos[v] = pos[v] + (avg(neighbors(v)) − pos[v]) × strength
 *
 * Neighbors are all vertices sharing a triangle edge with v (the "1-ring").
 * Uniform weights keep the implementation O(V + F) per iteration and
 * deterministic. Boundary vertices (open-mesh edges) are included without
 * special treatment, matching Blender's Smooth modifier behavior.
 *
 * ## Vertex group restriction
 *
 * When `vertexGroupIndices` is set, only vertices in the group are moved.
 * Unmasked vertices are frozen in place but still participate as neighbors
 * in the Laplacian sum — this anchors the smoothed region to its boundary
 * without producing a discontinuity at the group edge.
 *
 * ## Provenance
 *
 * Vertex count and layout are unchanged; provenance is identity.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import { projectVertexGroupThroughProvenance } from "./helpers.ts";
import type { ModifierGeometry } from "./modifierTypes.ts";

export function smoothCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "smooth" }>,
): ModifierGeometry {
  const vertCount = geo.positions.length / 3;

  // Nothing to do for empty or wire-only geometry.
  if (vertCount === 0 || geo.indices.length === 0) {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  const iterations = Math.max(1, Math.min(10, mod.iterations | 0));
  const strength = Math.max(0, Math.min(1, mod.strength));

  if (strength === 0) {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  // Empty result after remap (all group verts were removed by a prior
  // modifier) falls back to whole-mesh smooth to avoid a silent no-op.
  let allowed =
    projectVertexGroupThroughProvenance(
      mod.vertexGroupIndices,
      geo.provenance,
    ) ?? undefined;
  if (allowed && allowed.size === 0) allowed = undefined;

  // ---------------------------------------------------------------------------
  // Build uniform-weight adjacency (1-ring) in CSR layout.
  //
  // Strategy:
  //   1. Accumulate one Set<number> per vertex from the triangle fan.
  //   2. Convert to flat Uint32Array offsets + data for fast inner-loop access.
  //
  // The Set deduplication step is O(F) time and O(V + E) space. The resulting
  // CSR arrays live on the stack for this call and are not retained after the
  // function returns.
  // ---------------------------------------------------------------------------
  const neighborSets = new Array<Set<number>>(vertCount);
  for (let i = 0; i < vertCount; i++) neighborSets[i] = new Set();

  const indices = geo.indices;
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i],
      b = indices[i + 1],
      c = indices[i + 2];
    // Degenerate triangle guard — skip if any two verts are the same index.
    if (a === b || b === c || a === c) continue;
    neighborSets[a].add(b);
    neighborSets[a].add(c);
    neighborSets[b].add(a);
    neighborSets[b].add(c);
    neighborSets[c].add(a);
    neighborSets[c].add(b);
  }

  // Pack into CSR.
  const neighborOffsets = new Uint32Array(vertCount + 1);
  for (let i = 0; i < vertCount; i++) {
    neighborOffsets[i + 1] = neighborOffsets[i] + neighborSets[i].size;
  }
  const neighborData = new Uint32Array(neighborOffsets[vertCount]);
  for (let i = 0; i < vertCount; i++) {
    let j = neighborOffsets[i];
    for (const nb of neighborSets[i]) neighborData[j++] = nb;
  }

  // ---------------------------------------------------------------------------
  // Iterative Laplacian smoothing.
  //
  // Two buffers (positions ↔ smoothed) are swapped after each pass so each
  // iteration reads from the previous pass's output — a Jacobi-style update.
  // This produces a symmetric result regardless of vertex ordering, unlike a
  // Gauss-Seidel (in-place) update.
  // ---------------------------------------------------------------------------
  let positions = new Float32Array(geo.positions); // working copy — never mutates input
  let smoothed = new Float32Array(geo.positions.length);

  for (let iter = 0; iter < iterations; iter++) {
    for (let v = 0; v < vertCount; v++) {
      const base = v * 3;

      if (allowed && !allowed.has(v)) {
        // Frozen vertex — copy through unchanged.
        smoothed[base] = positions[base];
        smoothed[base + 1] = positions[base + 1];
        smoothed[base + 2] = positions[base + 2];
        continue;
      }

      const start = neighborOffsets[v];
      const end = neighborOffsets[v + 1];
      const count = end - start;

      if (count === 0) {
        // Isolated vertex (no triangle edges) — unchanged.
        smoothed[base] = positions[base];
        smoothed[base + 1] = positions[base + 1];
        smoothed[base + 2] = positions[base + 2];
        continue;
      }

      let sumX = 0,
        sumY = 0,
        sumZ = 0;
      for (let j = start; j < end; j++) {
        const nb = neighborData[j] * 3;
        sumX += positions[nb];
        sumY += positions[nb + 1];
        sumZ += positions[nb + 2];
      }
      const inv = 1 / count;
      smoothed[base] =
        positions[base] + (sumX * inv - positions[base]) * strength;
      smoothed[base + 1] =
        positions[base + 1] + (sumY * inv - positions[base + 1]) * strength;
      smoothed[base + 2] =
        positions[base + 2] + (sumZ * inv - positions[base + 2]) * strength;
    }

    // Swap buffers: smoothed becomes the input for the next pass.
    const tmp = positions;
    positions = smoothed;
    smoothed = tmp;
  }

  return {
    ...geo,
    positions,
    // Stored normals are derived from positions and are now stale; clear them
    // so the renderer recomputes correct face-weighted vertex normals.
    normals: null,
    provenance: identityProvenance(vertCount),
  };
}
